import React, { useContext, useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import * as XLSX from "xlsx";
import { ConfigContext } from "../../Context/ConfigContext";
import axios from "axios";
import { motion } from "framer-motion";

const ImportProducts = () => {
    const { apiUrl, apiHeadersJson, apiHeadersFile, store_id } = useContext(ConfigContext);
    const toast = useRef(null);
    const fileUploadRef = useRef(null);

    const [stores, setStores] = useState([]);
    const [categories, setCategories] = useState([]);
    const [importedData, setImportedData] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [proccested, setproccested] = useState(0);

    useEffect(() => {
        const fetchStores = async () => {
            try {
                const response = await axios.get(`${apiUrl}/Masters/GetStoreList`, {
                    headers: apiHeadersJson,
                });
                setStores(response.data.data || []);
            } catch (error) {
                showToast("error", "Error", "Failed to fetch stores");
            }
        };

        const fetchCategories = async () => {
            try {
                const response = await axios.get(`${apiUrl}/Masters/GetCategoryList`, {
                    headers: apiHeadersJson,
                });
                setCategories(response.data.data || []);
            } catch (error) {
                showToast("error", "Error", "Failed to fetch categories");
            }
        };

        fetchStores();
        fetchCategories();
    }, [apiUrl, apiHeadersJson]);

    const showToast = (severity, summary, detail) => {
        toast.current.show({ severity, summary, detail });
    };

    const convertStringToNumber = (value) => {
        // Handle null, undefined, empty string
        if (value === null || value === undefined || value === '') {
            return null;
        }

        // Convert to number
        const numberValue = Number(value);

        // Check if conversion resulted in NaN
        if (isNaN(numberValue)) {
            return null;
        }

        return numberValue;
    };

    // --- Process uploaded Excel file ---
    const handleFileUpload = (e) => {
        const file = e.files[0];
        if (!file) return;
        setIsUploading(true);

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: "binary" });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws, { defval: "" });

                const formatted = data.map((row, index) => ({
                    id: index + 1,
                    store_id: row.store_id || store_id,
                    category_id: convertStringToNumber(row.category_id) || "",
                    product_name: row.product_name || "",
                    product_barcode: row.product_barcode || "",
                    product_purchase_rate: row.product_purchase_rate || 0,
                    product_sale_rate: row.product_sale_rate || 0,
                    product_retail_rate: row.product_retail_rate || 0,
                    product_show: row.product_show === 1 || row.product_show === "Yes" ? 1 : 0,
                    product_for_order: row.product_for_order === 1 || row.product_for_order === "Yes" ? 1 : 0,
                    product_min_alert: row.product_min_alert || 0,
                    product_image: row.product_image || "" // ✅ only filename
                }));

                setImportedData(formatted);
                showToast("success", "Success", "File uploaded successfully");

                if (fileUploadRef.current) {
                    fileUploadRef.current.clear();
                }
            } catch (error) {
                showToast("error", "Error", "Failed to process the uploaded file");
            } finally {
                setIsUploading(false);
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleInputChange = (id, field, value) => {
        setImportedData((prev) =>
            prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
        );
    };

    const downloadSampleExcel = () => {
        const sampleData = [
            {
                store_id: Number(store_id),
                category_id: "",
                product_name: "Sample Product",
                product_barcode: "123456789",
                product_purchase_rate: 10,
                product_sale_rate: 15,
                product_retail_rate: 20,
                product_show: "Yes",
                product_for_order: "Yes",
                product_min_alert: 5,
                product_image: "sample.jpg"
            }
        ];

        const ws = XLSX.utils.json_to_sheet(sampleData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Products");
        XLSX.writeFile(wb, "product_import_template.xlsx");
        showToast("success", "Success", "Sample file downloaded");
    };

    const handleSubmit = async () => {
        if (importedData.length === 0) {
            showToast("warn", "Warning", "No data to submit");
            return;
        }

        // Validate required fields
        const invalidRows = importedData.filter(row => !row.store_id || !row.category_id || !row.product_name);
        if (invalidRows.length > 0) {
            showToast("error", "Validation Error", "Please fill all required fields");
            return;
        }

        // Validate rates
        const invalidRates = importedData.filter(row =>
            row.product_purchase_rate < 0 ||
            row.product_sale_rate <= 0 ||
            row.product_retail_rate <= 0
        );
        if (invalidRates.length > 0) {
            showToast("error", "Validation Error", "Product rates cannot be negative or 0");
            return;
        }

        // Check duplicates
        const barcodes = importedData.map(row => row.product_barcode);
        const duplicateBarcodes = [...new Set(barcodes.filter((b, i) => b && barcodes.indexOf(b) !== i))];
        if (duplicateBarcodes.length > 0) {
            showToast("error", "Validation Error", `Duplicate barcodes: ${duplicateBarcodes.join(", ")}`);
            return;
        }

        setIsSubmitting(true);
        try {
            const productsArray = importedData.map(product => ({
                store_id: product.store_id,
                category_id: product.category_id,
                product_name: product.product_name,
                product_barcode: product.product_barcode || "",
                product_purchase_rate: product.product_purchase_rate,
                product_sale_rate: product.product_sale_rate,
                product_retail_rate: product.product_retail_rate,
                product_show: product.product_show,
                product_for_order: product.product_for_order,
                product_min_alert: product.product_min_alert,
                product_image: product.product_image || "",
            }));


            const response = await axios.post(`${apiUrl}/Masters/ImportBulkProducts`, { products: productsArray }, {
                headers: apiHeadersJson,
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setproccested(progress);
                }
            });

            if (response.data.success) {
                showToast("success", "Success", response.data.message || "Products imported");
                setImportedData([]);
                if (fileUploadRef.current) fileUploadRef.current.clear();
            } else {
                showToast("error", "Error", response.data.message || "Failed to import products");
            }
        } catch (error) {
            showToast("error", "Error", "Failed to import products");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="container-fluid">
            <Toast ref={toast} />

            <motion.div className="d-flex justify-content-between align-items-center my-4"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}>
                <h3 className="fw-bold">Import Products</h3>
                <h6 className="text-muted">Masters / Import Products</h6>
            </motion.div>

            <div className="row">
                <div className="col-md-6 col-sm-12">
                    <FileUpload
                        ref={fileUploadRef}
                        name="file"
                        accept=".xlsx,.csv"
                        mode="advanced"
                        chooseLabel="Browse Files"
                        uploadLabel="Upload"
                        cancelLabel="Cancel"
                        customUpload
                        uploadHandler={handleFileUpload}
                        emptyTemplate={
                            <div className="d-flex flex-column align-items-center p-4">
                                <div className="d-flex">
                                    <i className="pi pi-cloud-upload text-4xl text-400 mb-3"></i>
                                    <i className="pi pi-download text-4xl text-400 ms-5" onClick={downloadSampleExcel}></i>
                                </div>
                                <p className="mb-2">Drag and drop files here to upload</p>
                                <p className="text-500 text-sm">Supports .xlsx, .csv files</p>
                            </div>
                        }
                    />
                </div>

                <div className="col-md-6 col-sm-12 d-flex flex-wrap align-items-end justify-content-end mt-3">
                    <Button label="Cancel" icon="pi pi-times" className="p-button-secondary mr-2"
                        onClick={() => setImportedData([])} />
                    <Button label={isSubmitting ? "Submitting..." : "Submit"}
                        icon="pi pi-check"
                        className="p-button-success"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        loading={isSubmitting} />
                </div>

                {isSubmitting && <span className="mt-2">{100 - proccested}% Remaining</span>}
            </div>

            {isUploading &&
                <div className="mt-3 p-3">
                    <div className="mb-3 p-3 bg-info text-white border-round">
                        <i className="pi pi-info-circle mr-2"></i>
                        Uploading Please Wait ...!
                    </div>
                </div>}

            {(importedData.length > 0 && !isUploading) && (
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }} className="mt-3">
                    <div className="mb-3 p-3 bg-info text-white border-round">
                        <i className="pi pi-info-circle mr-2"></i>
                        Found {importedData.length} products. Please review before submitting.
                    </div>

                    <DataTable value={importedData} responsiveLayout="scroll"
                        className="p-datatable-striped p-datatable-gridlines mb-4" scrollable>
                        <Column header="Store" body={(row) => (
                            <Dropdown value={row.store_id}
                                options={stores.map(s => ({ label: s.store_name, value: s.store_id }))}
                                onChange={(e) => handleInputChange(row.id, "store_id", e.value)}
                                placeholder="Select Store" className="w-100" />
                        )} />

                        <Column header="Category" body={(row) => (
                            <Dropdown value={row.category_id}
                                options={categories.map(c => ({ label: c.category_name, value: c.category_id }))}
                                onChange={(e) => handleInputChange(row.id, "category_id", e.value)}
                                placeholder="Select Category" className="w-100" />
                        )} />

                        <Column header="Product Name" body={(row) => (
                            <InputText value={row.product_name}
                                onChange={(e) => handleInputChange(row.id, "product_name", e.target.value)}
                                placeholder="Product Name" />
                        )} />

                        <Column header="Barcode" body={(row) => (
                            <InputText value={row.product_barcode}
                                onChange={(e) => handleInputChange(row.id, "product_barcode", e.target.value)}
                                placeholder="Barcode" />
                        )} />

                        <Column header="Purchase Rate" body={(row) => (
                            <InputNumber value={row.product_purchase_rate}
                                onValueChange={(e) => handleInputChange(row.id, "product_purchase_rate", e.value)} />
                        )} />

                        <Column header="Sale Rate" body={(row) => (
                            <InputNumber value={row.product_sale_rate}
                                onValueChange={(e) => handleInputChange(row.id, "product_sale_rate", e.value)} />
                        )} />

                        <Column header="Retail Rate" body={(row) => (
                            <InputNumber value={row.product_retail_rate}
                                onValueChange={(e) => handleInputChange(row.id, "product_retail_rate", e.value)} />
                        )} />

                        <Column header="Show" body={(row) => (
                            <input type="checkbox" checked={row.product_show === 1}
                                onChange={(e) => handleInputChange(row.id, "product_show", e.target.checked ? 1 : 0)} />
                        )} />

                        <Column header="For Order" body={(row) => (
                            <input type="checkbox" checked={row.product_for_order === 1}
                                onChange={(e) => handleInputChange(row.id, "product_for_order", e.target.checked ? 1 : 0)} />
                        )} />

                        <Column header="Min Alert" body={(row) => (
                            <InputNumber value={row.product_min_alert}
                                onValueChange={(e) => handleInputChange(row.id, "product_min_alert", e.value)} />
                        )} />

                        <Column header="Image Filename" body={(row) => (
                            <InputText value={row.product_image}
                                onChange={(e) => handleInputChange(row.id, "product_image", e.target.value)}
                                placeholder="image.jpg" />
                        )} />
                    </DataTable>
                </motion.div>
            )}
        </div>
    );
};

export default ImportProducts;
